/*
 * Leitura de datas das telas de viagem. Toda data aqui vira um `Date` à
 * MEIA-NOITE LOCAL (ou no wall-clock local, para data-hora), e nunca passa por
 * instante UTC: `toISOString` e `new Date('YYYY-MM-DD')` mudam o dia civil perto
 * da meia-noite em qualquer fuso a oeste de Greenwich (America/Sao_Paulo).
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;
const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/** `Date` local → `YYYY-MM-DD` do mesmo dia civil. Hoje é
 *  `toDateOnly(new Date())`: o fuso do RUNTIME, o dia de quem está olhando. */
export function toDateOnly(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `Date` → `YYYY-MM-DDTHH:mm` no relógio local, o valor dos campos de data-hora e a
 *  forma que `parseLocalDateTime` lê de volta (segundos e fuso ficam de fora). */
export function toLocalDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${toDateOnly(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `YYYY-MM-DD` (o que vier depois é ignorado, então um ISO com hora também
 *  serve) → meia-noite local daquele dia. `null` quando não casa ou quando o dia
 *  não existe (`2026-02-30`, `2026-13-01`): a data pode vir de provider ou de
 *  LLM, e um rollover silencioso daria outro dia com outro dia da semana. */
export function parseDateOnly(value: string | null | undefined): Date | null {
  const m = value ? DATE_ONLY.exec(value) : null;
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const date = new Date(year, month, day);
  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day
    ? date
    : null;
}

/** Data-hora local-naive (`2026-07-10T05:35`) → o mesmo wall-clock no fuso do
 *  runtime — o horário do aeroporto ou do balcão, como veio. Só o prefixo conta:
 *  segundos e um offset que venham depois são ignorados DE PROPÓSITO, porque os
 *  dígitos já são o relógio de parede do lugar (o trip-panel passa `departAt`
 *  com offset para lê-lo assim). `null` quando não casa ou quando o dia ou a
 *  hora não existem (`2026-02-30T05:35`, `T25:00`), como em `parseDateOnly`. */
export function parseLocalDateTime(value: string | null | undefined): Date | null {
  const m = value ? LOCAL_DATE_TIME.exec(value) : null;
  const date = m ? parseDateOnly(value) : null;
  if (!m || !date) return null;
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  if (hour > 23 || minute > 59) return null;
  date.setHours(hour, minute);
  return date;
}

/** Minutos desde a meia-noite do relógio de uma data-hora em texto (o `THH:MM`
 *  cru, sem conversão de fuso). `null` quando não casa — os filtros de horário
 *  dos resultados de voo ignoram a oferta. */
export function clockMinutes(value: string): number | null {
  const m = /T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Data-só gravada à meia-noite UTC → o MESMO dia no calendário local. Sem isso
 *  um fuso negativo renderiza o dia anterior. */
export function calendarDateFromUtcDay(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** Meia-noite local do dia de `date` — a base de toda comparação de "mesmo
 *  dia". */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Dias de calendário de `from` até `to` pelas datas ISO (sem fuso): 0 quando a
 *  data é a mesma, não parseia ou vem antes. É a conta do "+N" de chegada em dia
 *  seguinte e das "N noites" do planner — uma só, porque são a mesma. */
export function daysBetween(from: string, to: string): number {
  const a = parseDateOnly(from);
  const b = parseDateOnly(to);
  if (!a || !b) return 0;
  // `round`: um dia com horário de verão tem 23 ou 25 horas no fuso local.
  const days = Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000);
  return Math.max(days, 0);
}
